import * as React from 'react';
import { Box, Container, Typography, useTheme, styled } from '@mui/material';
import { keyframes } from '@emotion/react';
import StarIcon from '@mui/icons-material/Star';
import { FormatQuoteOutlined } from '@mui/icons-material';

const scrollLeft = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const scrollRight = keyframes`
  0% { transform: translateX(-50%); }
  100% { transform: translateX(0); }
`;

const MarqueeBox = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'direction',
})(({ direction }) => ({
    display: 'flex',
    width: 'fit-content',
    animation: `${direction === 'left' ? scrollLeft : scrollRight} 80s linear infinite`,
    animationPlayState: 'running',
    '&:hover': {
        animationPlayState: 'paused',
    },
}));

const TestimonialCard = ({ quote, name, image }) => {
    const theme = useTheme();
    const lightViolet = '#F3E8FF';

    return (
        <Box
            sx={{
                backgroundColor: '#eef0f5ff',
                borderRadius: 3,
                p: 3,
                border: '1px solid',
                borderColor: '#e6e3f7ff',
                boxShadow: theme.shadows[1],
                transition: 'box-shadow 0.3s',
                minWidth: 360,
                maxWidth: 390,
                flexShrink: 0,
                mr: 2,
                position: 'relative',
                overflow: 'hidden',
                height: 360,
                display: 'flex',
                flexDirection: 'column',
                '&:hover': {
                    boxShadow: theme.shadows[4],
                    transform: 'scale(1.04)',
                },
            }}
        >
            <FormatQuoteOutlined
                sx={{
                    width: 66,
                    height: 66,
                    color: '#8aa5ceff',
                    mb: 2,
                }}
            />
            <Typography
                variant="body1"
                sx={{
                    color: 'rgb(0, 0, 0)',
                    fontStyle: 'italic',
                    mb: 2,
                    flexGrow: 1,
                    fontSize: '0.95rem',
                    textAlign: 'center'
                }}
            >
                {quote}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, pt: 2, borderTop: `1px solid ${lightViolet}` }}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: lightViolet,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.2rem',
                        flexShrink: 0,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        component="img"
                        src={image}
                        alt={name}
                        sx={{
                            width: '100%',
                            height: '100%',
                            objectPosition: 'center'
                        }}
                    />
                </Box>
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.grey[900], lineHeight: 1.2 }}>
                        {name}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};
const rawTestimonials = [
    {
        quote: 'NCPro stands out for its excellent user-friendliness and impressive processing speed. Our team was able to adapt quickly with minimal training, and the system performs smoothly even with regular usage.',
        name: 'SRI SAKTHI INDUSTRIES', image: 'images/clients-logo/sriSakthi.png'
    },
    {
        quote: 'This product is extremely comfortable and practical for quality professionals to handle. The workflow is well designed, making non-conformance management simple and structured. It has made our quality processes more organized and efficient.',
        name: 'SREENIDHI TOOLS', image: 'images/clients-logo/sreenidhiTools.png'
    },
    {
        quote: 'It has been very useful in closing non-conformances within a short time. The reporting module is exceptionally fast and accurate, allowing us to generate reports quickly for internal reviews and customer submissions.',
        name: 'SHANTALA AUTOMATION', image: 'images/clients-logo/shantalaAuto.png'
    },
    {
        quote: 'NCPro is a highly effective software for industrial applications. We especially appreciate the company’s proactive approach in continuously upgrading the platform based on client feedback. ',
        name: 'SHANTHALA SPHEROCAST', image: 'images/clients-logo/shanthalaSphero.jpg'
    },
    {
        quote: 'The platform is simple, clean, and easy to navigate, enabling our team to focus on problem-solving rather than software complexity.',
        name: 'SAI KRUPA SPHEROCAST', image: 'images/clients-logo/saiKrupa.png'
    },
    {
        quote: 'Offering a lifetime license in today’s software market is a remarkable initiative. NCPro is a great start and is truly awesome to use.',
        name: 'TECHNO RINGS', image: 'images/clients-logo/technoRings.png'
    },
    {
        quote: 'NCPro has been highly useful for our quality team, particularly in learning how to handle non-conformance using AI-driven insights.',
        name: 'PRECISION MACHINE & AUTO COMPONENTS', image: 'images/clients-logo/precisionMachine.png'
    },
    {
        quote: 'A well-designed product that delivers strong benefits. The system is fast, responsive, and consistent across all modules. The overall performance and quick response time are very impressive.',
        name: 'REC FLOW TECHNOLOGIES', image: 'images/clients-logo/recflow.png'
    },
    {
        quote: 'NCPro provides a clear and structured way to handle non-conformance. The dashboard was further enhanced based on our inputs, which significantly improved usability and visibility.',
        name: 'IH CASTINGS', image: 'images/clients-logo/IHCastings.png'
    },
    {
        quote: 'NCPro is a strong and reliable product for managing quality processes. Its features, ease of use, and performance make it a highly recommended solution.',
        name: 'GLOBUS SPARES', image: 'images/clients-logo/globusSpares.png'
    },

];

const marqueeContentRow1 = [...rawTestimonials.slice(0, 5), ...rawTestimonials.slice(0, 5)];
const marqueeContentRow2 = [...rawTestimonials.slice(5, 10), ...rawTestimonials.slice(5, 10)];


export default function InfiniteTestimonials() {
    const theme = useTheme();
    const purple700 = '#6D28D9';

    return (
        <Box
            component="section"
            id="testimonials"
            sx={{
                py: { xs: 8, md: 12 },
                backgroundColor: 'white',
                overflow: 'hidden',
            }}
        >
            <Container maxWidth="xl" sx={{ px: 3 }}>
                <Box sx={{ textAlign: 'center', maxWidth: 680, mx: 'auto', mb: { xs: 8, md: 8 } }}>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: purple700,
                            px: 2,
                            py: 1,
                            borderRadius: 999,
                            mb: 3,
                            color: 'white',
                            boxShadow: theme.shadows[3],
                        }}
                    >
                        <StarIcon sx={{ width: 16, height: 16 }} />
                        <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                            Rated 4/5 by over 100+ users
                        </Typography>
                    </Box>
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: '2.5rem', md: '3rem' },
                            fontWeight: 700,
                            color: theme.palette.grey[900],
                            mb: 2,
                            lineHeight: 1.1,
                        }}
                    >
                        Words of praise from others
                        <br />
                        about our presence.
                    </Typography>
                </Box>
            </Container>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, justifyContent: 'center' }}>
                <MarqueeBox direction="left" sx={{ display: 'flex', gap: 1.5 }}>
                    {marqueeContentRow1.map((t, index) => (
                        <TestimonialCard key={`row1-${index}`} {...t} />
                    ))}
                </MarqueeBox>

                <MarqueeBox direction="right" sx={{ mt: 0, display: 'flex', gap: 1.5 }}>
                    {marqueeContentRow2.map((t, index) => (
                        <TestimonialCard key={`row2-${index}`} {...t} />
                    ))}
                </MarqueeBox>
            </Box>
        </Box>
    );
}